import {AccelKeyMgr, Action} from "lib/commons/actions";
import {EventMgr, EventsMgr} from "lib/commons/events";
import {IDomLogical, IRegPointer, REG} from 'lib/commons/registry';
import {CDM} from "lib/commons/utils/cdm";

/**
 * Classe pour la construction d'un objet global "window.desk" destiné à coordonner les actions au niveau de la window.
 *
 * L'objectif de cette classe est de centraliser / mutualiser le code définissant le comportement d'un desk
 * tout en permettant une très grande souplesse dans l'adaptation de l'objet desk à chaque situation.
 *
 * Le principe général pour l'initialisation d'un desk est de toujours distinguer la déclaration
 * de l'exécution en deux phases isolées afin de permettre d'injecter des modifications par rapport
 * au comportement standard avant la phase d'exécution.
 *
 * L'initialisation est décomposée en deux étapes modifiables par des listes d'extPoints séparées:
 *  - 'desk:init' émit après chargement du DOM. Peut retourner une Promise<void> pour un init asynchrone.
 *  - 'desk:load' émit après chargement de la window. Peut retourner une Promise<void> pour un load asynchrone.
 *
 * La cloture du desk peut-être intercepté par:
 *  - 'desk:unload' émit au déchargement de la window.
 *  - Pour bloquer le déchargement (avant le unload), utiliser CanCloseDeskFeat.
 *
 * L'ajout des fonctionnalités (XxxDeskFeat) est implémenté sous forme de class abstraites qui agrègent des méthodes
 * et propriétés sur l'objet desk initial.
 *
 * @example
 * window.desk = CanCloseDeskFeat.add(new Desk());
 * ...
 * reg.addToList(Desk.LC_init, ...);
 * ...
 * await window.desk.launch();
 */
export class Desk {

	static electron = navigator.userAgent.indexOf("Electron") >= 0;

	static noMouse = (() => {try {return !matchMedia('(any-pointer:fine)').matches} catch (e) {return false}})();


	/**
	 * 1er event déclenché au chargement, correspondant au Dom event 'DOMContentLoaded'.
	 * Callback : () => void | Promise<void>
	 */
	static LC_init = 'desk:init';

	/**
	 * 2ème event déclenché au chargement, correspondant au Dom event 'load' et après l'exec asynchrone de tous les LC_init.
	 * Callback : () => void | Promise<void>
	 */
	static LC_load = 'desk:load';

	//A revoir
	// - en fonction des besoins réels de coordination entre les différentes actions
	// - en conformité avec les nouvelles règles du lifeCycle des pages: https://developers.google.com/web/updates/2018/07/page-lifecycle-api
	//static LC_unload = 'desk:unload';

	static LIST_accelKeys = 'desk:accelKeys';

	readonly name: string;

	state: 'start' | 'inited' | 'initFailed' | 'loaded' | 'loadFailed' | 'unloaded';

	/**
	 * Gestionnaire de racourcis clavier global activé automatiquement si à la fin du chargement (LC_load),
	 * au moin un racourci est déclaré dans la liste LIST_accelKeys.
	 */
	accelKeys: AccelKeyMgr<Window>;

	constructor(name?: string) {
		this.name = name || window.location.pathname.replace(/^.+\/(.+)\..*$/, "$1");
		this.state = 'start';
		REG.reg.addToList(Desk.LC_init, "skin", 1, () => {
			REG.reg.installSkin('basis');
		})
	}

	/**
	 * Lance l'initialisation et le chargement du desk après l'ajout et la surcharge des features du desk et des extPoints du registre.
	 * Retourne une promesse résolue à la fin du processus de load.
	 */
	async launch(): Promise<void> {
		if (Desk.electron) {
			function importsLstn(ev: MessageEvent) {
				if (ev.data.type == "client-imports") {
					const imports = ev.data.imports as string[];
					window.removeEventListener("message", importsLstn);
					Promise.all(imports.map((imp) => REG.reg.env.resolver.resolve(imp).importJs())).catch((e) => console.error(e));
				}
			}

			window.addEventListener("message", importsLstn);
			window.postMessage({type: "desk-launch-start"}, location.origin);
		}

		const initialized = new Promise<void>((resolve, reject) => {
			const init = async () => {
				try {
					const lstns = REG.reg.getList<() => void | Promise<void>>(Desk.LC_init);
					if (lstns) for (const lstn of lstns) {
						const result = lstn();
						if (result instanceof Promise) await result;
					}
					this.state = 'inited';
					resolve();
				} catch (e) {
					this.state = 'initFailed';
					reject(e);
				}
			};

			if (document.readyState != 'loading') init();
			else document.addEventListener("DOMContentLoaded", init);
		});

		const loaded = new Promise<void>((resolve, reject) => {
			const load = async () => {
				try {
					await initialized;
					const lstns = REG.reg.getList<() => void | Promise<void>>(Desk.LC_load);
					if (lstns) for (const lstn of lstns) {
						const result = lstn();
						if (result instanceof Promise) await result;
					}
					this.state = 'loaded';
					if (REG.reg.isListFilled(Desk.LIST_accelKeys)) {
						this.accelKeys = new AccelKeyMgr().initFromMapActions(REG.reg.getListAsMap<Action<Window>>(Desk.LIST_accelKeys));
						window.addEventListener('keydown', this.onGloablKeyDown);
					}
					resolve();
				} catch (e) {
					this.state = 'loadFailed';
					reject(e as Error);
				}
			};

			if (document.readyState == 'complete') load();
			else window.addEventListener("load", load);
		});

		return loaded;
	}

	protected onGloablKeyDown(this: void, ev: KeyboardEvent) {
		window.desk.accelKeys.handleKeyboardEvent(ev, window);
	}

	/** Affichage d'un simple message d'information. A surcharger en fonction de l'ihm. */
	showInfo(text: string) {
		console.log(text);
	}

	/** Utilisé par l'implémentation des DeskFeat pour enrichir l'objet desk. */
	assignProps(cstr: typeof Desk) {
		const from = cstr.prototype;
		for (const key of Object.getOwnPropertyNames(from)) {
			if (key !== 'constructor') Object.defineProperty(this, key, Object.getOwnPropertyDescriptor(from, key));
		}
	}

	/**
	 * Détection de compatibilité de features avec le navigateur.
	 */
	static compatTable = new Map<string, boolean | compatDetector>();

	static checkCompat(key: string): boolean {
		const res = this.compatTable.get(key);
		if (res == null) {
			console.warn("Check compat not found: ", key);
			return true;
		}
		if (typeof res === "boolean") return res;
		const result = res();
		this.compatTable.set(key, result);
		return result;
	}

	/** Tables des versions de compatibilité issues du navigator.userAgent. */
	static navVersions = navigator.userAgent.match(/\w+\/\d+/g).reduce((navVersions: Dict<number>, navVersion) => {
		const [nav, version] = navVersion.split('/');
		navVersions[nav] = parseInt(version);
		return navVersions;
	}, {}) as Dict<number>;

	/** Nom et url des navigateurs jugés compatibles. */
	static get knownNavCompat(): { name: string, url?: string }[] {
		if ("knownNavCompat" in window)
			return window.knownNavCompat;
		else
			return [];
	}

	/**
	 * A utiliser uniquement si absence de UiThemeDeskFeat complet (exemples : dialogues hors contexte, comme upload cid...)
	 */
	initDefaultTheme(url?: string) {
		const style = document.createElement("link");
		style.setAttribute("rel", "stylesheet");
		style.setAttribute("type", "text/css");
		style.setAttribute("href", url || "/@skin@/core/themes/light-bluegrey.css");
		document.head.appendChild(style);
	}

	isSimpleClickPrefered(from: Node | IDomLogical, ctx?: IRegPointer<any>): boolean {
		const reg = REG.findReg(from, ctx);
		return reg?.getUserData("simpleClickPrefered") == true || false;
	}
}

type compatDetector = () => boolean;


/**
 * Ajoute la feature canClose à un desk.
 *
 * Pour interroger si le desk peut être clos (retourne true ou false) :
 * if(CanCloseDeskFeat.isIn(desk)) desk.canClose(silentMode);
 *
 * Pour ajouter un listener pour empecher la fermeture du desk :
 * if(CanCloseDeskFeat.isIn(desk)) desk.canCloseLstn.on("canClose", () => { return false;});
 */
export class CanCloseDeskFeat extends Desk {

	/** Peut être ajouté après desk.launch(). */
	static add(desk: Desk): CanCloseDeskFeat {
		if (this.isIn(desk)) return desk;
		desk.assignProps(this);
		const d = desk as CanCloseDeskFeat;
		d.canCloseLstn = new EventsMgr<IDeskCanCloseEvents>();
		window.addEventListener("beforeunload", (ev: BeforeUnloadEvent) => {
			if (!d.canClose(true)) {
				ev.returnValue = "Vos actions en cours seront perdues, souhaitez-vous vraiment fermer cette page ?";
				return ev.returnValue;
			}
		});
		return d;
	}

	static isIn(desk: Desk): desk is CanCloseDeskFeat {return 'canClose' in desk}

	canCloseLstn: EventsMgr<IDeskCanCloseEvents>;

	canClose(silentMode?: boolean): boolean {
		return this.canCloseLstn.emitUntil('canClose', silentMode) !== false;
	}
}

/** Events pour CanCloseDeskFeat. */
type IDeskCanCloseEvents = {
	canClose(silentMode: boolean): undefined | false;
}


/**
 * Feature d'écoute et de manipulation du hash de l'URL sous une forme objet (sérialisation CDM).
 *
 */
export class UrlHashObjDeskFeat extends Desk {

	/** Peut être ajouté après desk.launch(). */
	static add(desk: Desk): UrlHashObjDeskFeat {
		if (this.isIn(desk)) return desk;
		desk.assignProps(this);
		const d = desk as UrlHashObjDeskFeat;
		d.onUrlHashChange = new EventMgr();
		window.addEventListener('hashchange', () => {d._updateFromHash(true)});
		d._updateFromHash(false);
		return d;
	}

	static isIn(desk: Desk): desk is UrlHashObjDeskFeat {return 'onUrlHashChange' in desk}

	/**
	 * Listener de changement du hash.
	 */
	onUrlHashChange: EventMgr<(oldV: Jsonisable, newV: Jsonisable) => Promise<void>>;

	/** Récupère le hash sous forme objet */
	getUrlHash(): Jsonisable {return this._urlHash}

	/**
	 * Impose une réécriture du hash de l'URL à partir d'un objet modifié.
	 * @param replace Pas d'historique. cf https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
	 */
	async setUrlHash(newVal: Jsonisable, replace?: boolean) {
		const old = this._urlHash;
		this._urlHash = newVal || {};
		this._urlHashSer = newVal ? CDM.stringify(this._urlHash) : null;
		if (replace) location.replace(newVal ? '#' + encodeURIComponent(this._urlHashSer) : '#');
		else location.hash = encodeURIComponent(this._urlHashSer || "");
		await this.onUrlHashChange.emitAsyncCatched(old, this._urlHash);
	}

	protected _updateFromHash(emit: boolean) {
		const newHashSer = location.hash ? decodeURIComponent(location.hash.substr(1)) : '';
		if (this._urlHashSer === newHashSer) return; //provient de nous
		const old = this._urlHash;
		try {
			this._urlHash = newHashSer ? CDM.parse(newHashSer) as Dict<any> : {};
			if (emit) this.onUrlHashChange.emitAsyncCatched(old, this._urlHash);
		} catch (e) {
			//hash invalide
		}
	}

	/** Hash parsé */
	protected _urlHash: Jsonisable;

	/** Hash en  CDM sérialisée, avant encodeURIComponent() */
	protected _urlHashSer: string;

}


/**
 * Feature qui permet de détecter toute activité du user et de basculer dans un état "inactive"
 * au bout d'un timeout (3 minutes par défaut) lorsqu'aucune activité n'est détectée.
 * A l'initialisation du desk, le user est considéré "active".
 *
 * L'implémentation évalue cette (non) activité tous les 'checkInterval' (10s par défaut).
 * L'eventMgr 'userActiveLstn' permet d'être notifié au changement de statut, ou systématiquement
 * tous les 'checkInterval' avec la durée approximative (à 'checkInterval' près ) de la dernière
 * détection d'activité du user.
 *
 * Par défaut, la détection écoute les events 'mousemove', 'touchstart', 'keypress', 'focus'
 * et 'visibilitychange' sur la window.
 * D'autres moyens de détections de l'activité peuvent être utilisés, il suffit alors d'appeler
 * desk.markAsUserActive().
 */
export class UserActiveDeskFeat extends Desk {

	/** Peut être ajouté après desk.launch(), mais activeTimeout et checkInterval ne sont pas modifiés. */
	static add(desk: Desk, activeTimeout = 180000, checkInterval = 10000): UserActiveDeskFeat {
		if (this.isIn(desk)) return desk;
		desk.assignProps(this);
		const d = desk as UserActiveDeskFeat;
		// mémoire de la config
		d.activeTimeout = activeTimeout;
		//d.checkInterval = checkInterval;
		d.userActiveLstn = new EventsMgr<IDeskUserActiveEvents>();
		//actif par défaut à l'init.
		d._mark = true;
		d._lastMark = Date.now();
		d._isUserActive = true;
		setInterval(() => {
			const now = Date.now();
			const delta = now - d._lastMark;
			d.userActiveLstn.emit('cyclicNotif', delta);
			if (d._isUserActive && delta > activeTimeout) {
				// on passe en statut inactif.
				d._isUserActive = false;
				d.userActiveLstn.emit('inactive', delta);
			}
			d._mark = false;
		}, checkInterval);
		const markActive = d.markAsUserActive.bind(d);
		window.addEventListener('mousemove', markActive, true);
		window.addEventListener('touchstart', markActive, true);
		window.addEventListener('keypress', markActive, true);
		window.addEventListener('focus', markActive, true);
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') d.markAsUserActive()
		}, true);
		return d;
	}

	static isIn(desk: Desk): desk is UserActiveDeskFeat {return 'markAsUserActive' in desk}

	activeTimeout: number;
	//checkInterval: number; à revoir pour être modifiable (setInterval à annuler et recréer)

	userActiveLstn: EventsMgr<IDeskUserActiveEvents>;

	get isUserActive() {return this._isUserActive}

	protected _isUserActive: boolean;
	protected _mark: boolean;
	protected _lastMark: number;

	/** A appeler à chaque event marquant l'activité du user. */
	markAsUserActive() {
		if (!this._mark) {
			this._mark = true;
			this._lastMark = Date.now();
			if (!this._isUserActive) {
				//on était en état inactif, on bascule.
				this._isUserActive = true;
				this.userActiveLstn.emit('active');
			}
		}
	}

}

/** Events pour UserActiveDeskFeat. */
type IDeskUserActiveEvents = {

	/** Appelé régulièrement, en fournissant la durée en ms depuis la dernière marque d'activité. */
	cyclicNotif(lastMarkSince: number): void

	/**
	 * Appelé 1 fois lorsque le seuil de la durée d'inactivité est dépassé.
	 * @param since durée en ms depuis la dernière marque d'activité
	 */
	inactive(since: number): void

	/** Appelé 1 fois lors de la 1ère marque d'activité alors que le desk était en statut inactif.  */
	active(): void
}


/**
 * Ajout de l'objet desk comme global
 */
declare global {
	let desk: Desk;

	interface Window {
		knownNavCompat?: { name: string, url?: string }[];
		desk: Desk;
	}
}
